import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
// Models
import IPropertyOwnership from "models/PropertyOwnership";
// Hooks
import { useAppDispatch } from "hooks/redux";
import useDialog from "hooks/useDialog";
// Async
import PropertyOwnershipsAsync from "store/propertyOwnerships/propertyOwnershipsAsync";
// Actions
import { PropertyOwnershipsActions } from "store/propertyOwnerships/propertyOwnershipsSlice";
// components
import Title from "components/Title";
import PropertyOwnershipImages from "./PropertyOwnershipImages";
import ReservationRequestForm from "components/ReservationRequest.form";
// Selectors
import { selectPropertyOwnershipDetails, selectPropertyOwnerships } from "store/propertyOwnerships/propertyOwnershipsSelectors";
// mui
import { makeStyles } from '@mui/styles';
import {
  Box, Button, Chip, Divider, Grid, LinearProgress, Typography
} from "@mui/material";
// icons
import {
  EventAvailableOutlined as EventAvailableOutlinedIcon,
  RequestQuoteOutlined as RequestQuoteOutlinedIcon,
  PublicOutlined as PublicOutlinedIcon,
  WavesOutlined as WavesOutlinedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  BedOutlined as BedOutlinedIcon,
  TravelExploreOutlined as TravelExploreOutlinedIcon,
  BathroomOutlined as BathroomOutlinedIcon,
  ImageOutlined as ImageOutlinedIcon,
} from '@mui/icons-material';
// utilities
import { formatStartEndDate } from "utilities/DateTimeFormatter";
// Swiper
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
// Utilities
import { getContent } from 'utilities/Utilities';

const PropertyOwnershipDetails:React.FC = () => {
  const classes = useStyles();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const propertyOwnershipId = useParams()['propertyOwnershipId'];

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const propertyOwnership = useSelector(selectPropertyOwnershipDetails);
  const propertyOwnerships = useSelector(selectPropertyOwnerships)?.filter((ownership: IPropertyOwnership) => ownership.id !== propertyOwnership?.id );


  const allImages = [...propertyOwnership?.property.images || [], ...propertyOwnership?.images || []];
  const [mainImage, setMainImage] = useState(!!allImages.length ? allImages[0].url : '/img/noPropertyFound.png');

  useEffect(() => {
    setMainImage(!!allImages.length ? allImages[0].url : '/img/noPropertyFound.png');
    if (propertyOwnership) {
      dispatch(PropertyOwnershipsAsync.fetchPropertyOwnerships({ propertyId: propertyOwnership?.property.id  }))
    }
    // eslint-disable-next-line
  }, [propertyOwnership]);

  useEffect(() => {
    setIsLoading(true);
    window.scrollTo({ top: 0 });
    if (propertyOwnershipId) {
      dispatch(PropertyOwnershipsAsync.fetchPropertyOwnership(+propertyOwnershipId))
        .unwrap()
        .catch(() => navigate('/timeshares'))
        .finally(() => setIsLoading(false));
    }
    // eslint-disable-next-line
  }, [propertyOwnershipId]);

  useEffect(() => {
    return () => {
      dispatch(PropertyOwnershipsActions.setPropertyOwnershipDetails(null));
    }
    // eslint-disable-next-line
  }, []);

  const { Dialog, openDialog, closeDialog } = useDialog();

  const { Dialog:ReservationRequestDialog, openDialog:openReservationRequestDialog, closeDialog:closeReservationRequestDialog } = useDialog();

  if (isLoading) return <LinearProgress />;
  if (!propertyOwnership) return null;

  return (
    <React.Fragment>
      <Dialog maxWidth="lg">
        <PropertyOwnershipImages onClose={closeDialog} images={allImages} />
      </Dialog>
      <ReservationRequestDialog maxWidth="md">
        <ReservationRequestForm onClose={closeReservationRequestDialog} />
      </ReservationRequestDialog>
      <Box sx={{ padding: '32px 32px 48px', minHeight: 'calc(100vh - 236px)', backgroundColor: '#f5f6f7' }}>
        <div className="container">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box>
              <Title>{propertyOwnership.property.name}</Title>
              <Chip
                label={propertyOwnership.start ? formatStartEndDate(propertyOwnership.start, propertyOwnership.end) : 'Flexible dates'}
                icon={<EventAvailableOutlinedIcon />}
                color={propertyOwnership.start ? 'default' : 'success'}
                size="small" sx={{ mt: 2, p:1 }}
              />
            </Box>
            <Button
              sx={{ display: { xs: 'none', sm: 'inline-flex' }, minWidth: 'max-content' }}
              variant="contained"
              startIcon={<RequestQuoteOutlinedIcon />}
              size="large"
              onClick={openReservationRequestDialog}
            >
              Request a quote
            </Button>
          </Box>

          <Box sx={{
            display: 'flex', mt: 3, flexDirection: { xs: 'column', md: 'row' },
            backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0px 4px 32px rgba(0, 0, 0, 0.08)'
          }}>
            <Box sx={{ position: 'relative', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              {allImages.length > 1 && (
                <Button
                  sx={{
                    position: 'absolute', top: '16px', right: '16px', backgroundColor: '#fff', color: '#222',
                    '&:hover': { backgroundColor: '#fafafa', boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.3)' }
                  }}
                  size="small"
                  startIcon={<ImageOutlinedIcon />}
                  onClick={openDialog}
                >
                  View all photos
                </Button>
              )}
              <img src={mainImage} className={classes.mainImage} alt={propertyOwnership.property.name} />
              {allImages.length > 1 && (
                <Box sx={{
                  width: '100%', height: '100px', '@media (min-width: 900px)': { maxWidth: '516px' },
                  margin: '0 auto', padding: '8px 0',
                }}>
                  <Swiper
                    spaceBetween={8}
                    slidesPerView={4}
                  >
                      
                    {allImages.slice(0,4).map((img) => (
                      <SwiperSlide key={img.url}>
                        <img src={img.url} className={classes.image}  alt={propertyOwnership.property.name} onClick={() => setMainImage(img.url)} />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </Box>
              )}
            </Box>
            <Box sx={{
              boxShadow: '0px 0px 0px 1px #E0E0E0', flexGrow: 1, p: 2, borderRadius: '0 0 8px 8px',
              maxWidth: { xs: '100%', md: '500px' }, '@media (min-width: 1240px)': { borderRadius: '0 8px 8px 0' } 
            }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                    <PublicOutlinedIcon />
                    <Box>
                      <Typography className={classes.subtitle}>Region</Typography>
                      <Typography className={classes.text}>{propertyOwnership.property.region}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                    <WavesOutlinedIcon />
                    <Box>
                      <Typography className={classes.subtitle}>View</Typography>
                      <Typography className={classes.text}>{propertyOwnership.view}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                    <PlaceOutlinedIcon />
                    <Box>
                      <Typography className={classes.subtitle}>Country</Typography>
                      <Typography className={classes.text}>{propertyOwnership.property.country}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                    <BedOutlinedIcon />
                    <Box>
                      <Typography className={classes.subtitle}>Bedrooms</Typography>
                      <Typography className={classes.text}>{propertyOwnership.bedrooms}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                    <TravelExploreOutlinedIcon />
                    <Box>
                      <Typography className={classes.subtitle}>{`${getContent('labels').labelDestinationTypeSingularText}(s)`}</Typography>
                      <Typography className={classes.text}>{propertyOwnership.property.destinationTypes.map(destinationType => destinationType.name).join(', ')}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                    <BathroomOutlinedIcon />
                    <Box>
                      <Typography className={classes.subtitle}>Bathrooms</Typography>
                      <Typography className={classes.text}>{propertyOwnership.bathrooms}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}><Divider sx={{ pt: 2 }} /></Grid>
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography className={classes.subtitle}>Description</Typography>
                  <Typography className={classes.text} sx={{ whiteSpace: 'pre-line' }}>{propertyOwnership.property.description}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Button
            fullWidth
            sx={{ display: { sm: 'none' }, mt: 2 }}
            variant="contained"
            startIcon={<RequestQuoteOutlinedIcon />}
            size="large"
            onClick={openReservationRequestDialog}
          >
            Request a quote
          </Button>

          {!!propertyOwnerships?.length && (
            <Box sx={{ pt: '40px' }}>
              <Typography sx={{ fontWeight: 500, fontSize: { xs: '16px', sm: '20px', lg: '24px' } }}>Other rentals from the same property</Typography>
              <Box sx={{ pt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {propertyOwnerships?.map((ownership: IPropertyOwnership) => (
                  <Button
                    sx={{ width: { xs: '100%', sm: 'max-content' }, backgroundColor: '#fff', boxShadow: '0px 4px 32px rgba(0, 0, 0, 0.08)', borderRadius: '4px' }}
                    key={ownership.id}
                    startIcon={<EventAvailableOutlinedIcon />}
                    onClick={() => navigate(`/timeshares/${ownership.id}`)}
                  >
                    {ownership.start ? formatStartEndDate(ownership.start, ownership.end) : 'Flexible dates'}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
        </div>
      </Box>
    </React.Fragment>
  )
}

export default PropertyOwnershipDetails;

const useStyles = makeStyles({
  mainImage: {
    width: '100%',
    height: '100%',
    flexGrow: 1,
    objectFit: 'cover',
    borderRadius: '8px 0 0 0',
    '@media (min-width: 900px)': {
      height: '280px',
    }
  },
  image: {
    flexGrow: 1,
    height: '100px',
    objectFit: 'cover',
    cursor: 'pointer',
    '@media (min-width: 900px)': {
      maxWidth: '123px',
    }
  },
  title: {},
  subtitle: {
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '166%',
    letterSpacing: '-0.1px',
    color: 'rgba(0, 0, 0, 0.6)',
  },
  text: {
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '143%',
    letterSpacing: '-0.43px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
})
